// docs/35 WL6 — the creatures' pixel art: tiny on purpose (two to eleven board pixels, against a
// 24 px square and a unit's ~18 px body) so nobody mistakes a mouse for a monster. Drawn once into
// one canvas texture at boot, frame `species:variant:pose`; art faces east, the layer flips it.
// Grids are rows of palette letters, `.` clear. Variants are palettes (a monarch, a cabbage white;
// a sparrow, a snow robin), picked by the row's seed and the room's kit.
import type Phaser from 'phaser';
import type { Species } from '../../core/wildlife/motion';
import { seedUnit } from '../../core/wildlife/motion';

interface Sheet { pals: ReadonlyArray<Record<string, string>>; frames: Record<string, readonly string[]> }

export const WILD_TEXTURE = 'fk-wild';
export const WILD_GLOW = 'fk-wild-glow';

const SHEETS: Record<Species, Sheet> = {
  butterfly: {
    pals: [
      { a: '#f4f1e4', b: '#bdb59c', k: '#3a3326' },   // cabbage white
      { a: '#f6d84c', b: '#d98f22', k: '#3a2a10' },   // brimstone
      { a: '#ec8a2c', b: '#3a2210', k: '#2a1a10' },   // monarch
      { a: '#79bdf4', b: '#2f5fa8', k: '#1c2440' },   // common blue
      { a: '#e65050', b: '#f2d65a', k: '#2a1010' },   // jungle red
    ],
    frames: {
      open: ['aa...aa', 'abakaba', 'aaakaaa', '.ba.ab.', '...k...'],
      half: ['.......', '.aa.aa.', '.abkba.', '..aka..', '...k...'],
      shut: ['...a...', '..aa...', '..ab...', '...k...', '...k...'],
    },
  },
  songbird: {
    pals: [
      { a: '#8a6a48', b: '#dccaa6', y: '#e3b445', k: '#3a2a1a' },   // sparrow
      { a: '#a48c3c', b: '#f0e072', y: '#e7a43a', k: '#3a2a1a' },   // finch
      { a: '#6e5c52', b: '#dc5c3c', y: '#e0b04a', k: '#2e2622' },   // robin
    ],
    frames: {
      stand: ['...ab.', 'aaaaay', '.aaa..', '..k...'],
      walk: ['...ab.', 'aaaaay', '.aaa..', '.k.k..'],
      peck: ['......', 'aaaab.', '.aaaa.', '..k.ay'],
      flyUp: ['..aa..', '...a..', 'aaaaby', '......'],
      flyDown: ['......', 'aaaaby', '..aa..', '..a...'],
    },
  },
  crow: {
    pals: [{ a: '#24242e', b: '#3c3c4c', y: '#55555e', k: '#141418' }],
    frames: {
      stand: ['....bb.', '...aaay', 'aaaaa..', '.aaaa..', '..k.k..'],
      walk: ['....bb.', '...aaay', 'aaaaa..', '.aaaa..', '.k..k..'],
      peck: ['.......', 'aaaa...', '.aaaab.', '.aaaa.y', '..k.k..'],
      flyUp: ['..aa...', '...aa..', 'aaaaaby', '.aaaa..', '.......'],
      flyDown: ['.......', 'aaaaaby', '.aaaa..', '..aa...', '..a....'],
    },
  },
  swallow: {
    pals: [{ a: '#262c44', b: '#c8b8a4' }],
    frames: {
      glide: ['aa...aa', '..aba..', '...a...'],
      flap: ['...a...', '.aabaa.', 'a..a..a'],
    },
  },
  hawk: {
    pals: [{ a: '#5c4232', b: '#c8a070', c: '#7c5a40' }],
    frames: {
      glide: ['a.........a', 'aac.....caa', '.aaacbcaaa.', '....aaa....'],
      flap: ['...........', 'aac.....caa', '.aaacbcaaa.', 'a...aaa...a'],
    },
  },
  bat: {
    pals: [{ a: '#2a2232', k: '#5e4a5c' }],
    frames: {
      a: ['a.....a', 'aa.k.aa', '..aaa..'],
      b: ['.......', '.aakaa.', 'a..a..a'],
    },
  },
  moth: {
    pals: [{ a: '#ddd2b4', k: '#8a7a60' }, { a: '#c8c0d8', k: '#6a6080' }],
    frames: {
      a: ['a.a', 'aka', '.k.'],
      b: ['.a.', 'aka', '.k.'],
    },
  },
  firefly: {
    pals: [{ a: '#eef68a', k: '#3a3a20' }],
    frames: { a: ['a', 'k'] },
  },
  mouse: {
    pals: [
      { a: '#8c8c94', b: '#c49494', n: '#e4a4a4', t: '#a48484', k: '#505058' },
      { a: '#9a8064', b: '#c8a08a', n: '#e4a4a4', t: '#a88a74', k: '#4c4036' },
    ],
    frames: {
      stand: ['...b.', 'taaan', '..k.k'],
      run: ['...b.', 'taaan', '.k..k'],
      sniff: ['....b', 'taaan', '..k.k'],
    },
  },
  rat: {
    pals: [{ a: '#6a5a50', b: '#8a7068', n: '#d09090', t: '#9a7a70', k: '#3a3030' }],
    frames: {
      stand: ['....bb.', 'ttaaaan', '...k.k.'],
      run: ['....bb.', 'ttaaaan', '..k...k'],
      sniff: ['.....bb', 'ttaaaan', '...k.k.'],
    },
  },
  spider: {
    pals: [{ a: '#3c3232', k: '#1a1414' }],
    frames: { a: ['k.k', 'kak', 'k.k'] },
  },
  scorpion: {
    pals: [{ a: '#c48c3c', b: '#8a5a20' }, { a: '#4a3a30', b: '#2a201a' }],
    frames: {
      stand: ['aa....', 'a.a..b', '.aaaab', 'a.a.a.'],
      walk: ['aa....', 'a.a..b', '.aaaab', '.a.a.a'],
      sting: ['.aa...', 'a..a.b', '.aaaab', 'a.a.a.'],
    },
  },
  lizard: {
    pals: [{ a: '#6a9a50', t: '#55803e', b: '#1e281a' }, { a: '#c8a868', t: '#a88a50', b: '#3a2a1a' }, { a: '#2a2422', t: '#e8642a', b: '#120c0a' }],   // green, sand, a salamander of the Ashlands
    frames: {
      stand: ['..a.a..', 'ttaaaab'],
      run: ['.a...a.', 'ttaaaab'],
      bob: ['..a.ab.', 'ttaaaa.'],
    },
  },
  gnats: { pals: [{ a: '#24221a' }], frames: { a: ['a'] } },
  dragonfly: {
    pals: [{ t: '#2a8a9a', a: '#3ab4c4', b: '#1a3a40', w: '#d6f4ff99' }, { t: '#b03a2a', a: '#d8583a', b: '#3a1a14', w: '#ffeedd99' }],
    frames: {
      a: ['..ww...', 'ttttaab', '..ww...'],
      b: ['.w..w..', 'ttttaab', '.w..w..'],
    },
  },
  frog: {
    pals: [{ a: '#4c8a3c', b: '#1e2e16', c: '#c8d890' }, { a: '#6a7a3a', b: '#1e2416', c: '#d8d8a0' }],
    frames: {
      sit: ['.b.b.', 'aaaaa', 'aa.aa'],
      puff: ['.b.b.', 'aaaaa', 'acaaa'],
      jump: ['.b.b.', 'aaaaa', 'a...a'],
    },
  },
};

/** Palette index for a creature: the seed's, nudged by the room (snow robins, sand lizards, jungle colours, Ashlands salamanders and black scorpions). */
export function variantOf(sp: Species, seed: number, kit: string): number {
  const n = SHEETS[sp].pals.length;
  const pick = (opts: readonly number[]) => opts[Math.floor(seedUnit(seed, 99) * opts.length)]!;
  if (sp === 'songbird') return kit === 'winterwood' ? 2 : pick([0, 0, 1]);
  if (sp === 'lizard' && kit === 'ashlands') return 2;
  if (sp === 'scorpion' && kit === 'ashlands') return 1;
  if (sp === 'lizard' || sp === 'scorpion') return kit === 'dunes' ? (sp === 'lizard' ? 1 : 0) : (sp === 'lizard' ? 0 : 1);
  if (sp === 'butterfly') return kit === 'jungle' ? pick([2, 3, 4]) : pick([0, 0, 1, 1, 2, 3]);
  if (sp === 'dragonfly') return pick([0, 0, 1]);
  return Math.floor(seedUnit(seed, 99) * n) % n;
}

export function frameKey(sp: Species, variant: number, pose: string): string { return `${sp}:${variant}:${pose}`; }

/** Pixel size of a species' frames. */
export function sizeOf(sp: Species): { w: number; h: number } {
  const f = Object.values(SHEETS[sp].frames)[0]!;
  return { w: f[0]!.length, h: f.length };
}

function rgba(hex: string): [number, number, number, number] {
  const h = hex.replace('#', '');
  const n = parseInt(h.slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255, h.length > 6 ? parseInt(h.slice(6, 8), 16) : 255];
}

/** Draws every frame into one canvas texture (idempotent per scene's texture manager). */
export function ensureWildTextures(scene: Phaser.Scene): void {
  const tm = scene.textures;
  if (!tm.exists(WILD_GLOW)) {
    const c = tm.createCanvas(WILD_GLOW, 16, 16)!;
    const g = c.context.createRadialGradient(8, 8, 0, 8, 8, 8);
    g.addColorStop(0, 'rgba(240,255,150,0.9)'); g.addColorStop(0.35, 'rgba(220,250,120,0.35)'); g.addColorStop(1, 'rgba(200,240,100,0)');
    c.context.fillStyle = g; c.context.fillRect(0, 0, 16, 16); c.refresh();
  }
  if (tm.exists(WILD_TEXTURE)) return;
  // lay frames out in rows, one row per species × variant
  const rows: Array<{ sp: Species; v: number; frames: Array<[string, readonly string[]]> }> = [];
  for (const [sp, sheet] of Object.entries(SHEETS) as Array<[Species, Sheet]>) {
    sheet.pals.forEach((_p, v) => rows.push({ sp, v, frames: Object.entries(sheet.frames) }));
  }
  const W = Math.max(...rows.map((r) => r.frames.reduce((a, [, f]) => a + f[0]!.length + 1, 0)));
  const H = rows.reduce((a, r) => a + r.frames[0]![1].length + 1, 0);
  const tex = tm.createCanvas(WILD_TEXTURE, W, H)!;
  const img = tex.context.createImageData(W, H);
  const placed: Array<[string, number, number, number, number]> = [];
  let y0 = 0;
  for (const r of rows) {
    const pal = SHEETS[r.sp].pals[r.v]!;
    let x0 = 0, rh = 0;
    for (const [pose, grid] of r.frames) {
      grid.forEach((line, yy) => [...line].forEach((ch, xx) => {
        const col = pal[ch];
        if (!col) return;
        const [cr, cg, cb, ca] = rgba(col);
        const o = ((y0 + yy) * W + x0 + xx) * 4;
        img.data[o] = cr; img.data[o + 1] = cg; img.data[o + 2] = cb; img.data[o + 3] = ca;
      }));
      placed.push([frameKey(r.sp, r.v, pose), x0, y0, grid[0]!.length, grid.length]);
      x0 += grid[0]!.length + 1; rh = Math.max(rh, grid.length);
    }
    y0 += rh + 1;
  }
  tex.context.putImageData(img, 0, 0);
  for (const [k, x, y, w, h] of placed) tex.add(k, 0, x, y, w, h);
  tex.refresh();
}

/** Every pose name of a species (tests, the gallery caption). */
export function posesOf(sp: Species): string[] { return Object.keys(SHEETS[sp].frames); }
